import { useState, useCallback, useRef, useEffect } from 'react';

const INFERNO_DROP_STRATEGEM_ID = 319;
export const NAPALM_DURATION_MS = 2200;

/**
 * Hook to trigger napalm animation over all 4 zones when Inferno Drop (strategem 319) is played.
 * Inferno Drop has no targeting, so it is executed immediately and never sends strategem-initiated.
 * We detect it from gameState when the strategem appears in completedOrders (same pattern as 113 shockwave).
 * @param {Object} gameState - Current game state
 * @param {string} userId - Current user's ID (for skip-first-render)
 * @param {(instanceId: string) => { holdTargets?: Array }|undefined|null} [getPendingBroadcastDamage] - Pre-hit hold metadata keyed by strategem instanceId
 * @returns {{ activeNapalms: Array<{ key: string, strategemInstanceId?: string, holdTargets?: Array }>, triggerInfernoDrop: Function, completeNapalm: Function }}
 */
export const useInfernoDropNapalm = (gameState, userId, getPendingBroadcastDamage = null) => {
  const [activeNapalms, setActiveNapalms] = useState([]);
  const processedRef = useRef(new Set());
  const isFirstRenderRef = useRef(true);

  const triggerInfernoDrop = useCallback((key, strategemInstanceId = null, holdTargets = null) => {
    if (!key) return;
    setActiveNapalms((prev) => [...prev, { key, strategemInstanceId, holdTargets }]);
  }, []);

  const completeNapalm = useCallback((key) => {
    setActiveNapalms((prev) => prev.filter((n) => n.key !== key));
  }, []);

  useEffect(() => {
    if (!gameState?.state || !userId) return;
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    const allPlayerIds = Object.keys(gameState.state || {});

    allPlayerIds.forEach((playerId) => {
      const player = gameState.state[playerId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!strategem || Number(strategem.id) !== INFERNO_DROP_STRATEGEM_ID) return;

        const strategemKey = `strategem-319-${strategem.instanceId}-${playerId}`;
        if (processedRef.current.has(strategemKey)) return;

        processedRef.current.add(strategemKey);

        const animationKey = `inferno-319-${strategem.instanceId}-${Date.now()}`;
        const pendingBroadcast = typeof getPendingBroadcastDamage === 'function'
          ? getPendingBroadcastDamage(strategem.instanceId)
          : null;
        triggerInfernoDrop(
          animationKey,
          strategem.instanceId,
          pendingBroadcast?.holdTargets ?? null
        );
      });
    });

    if (processedRef.current.size > 50) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 50).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, userId, triggerInfernoDrop, getPendingBroadcastDamage]);

  return {
    activeNapalms,
    triggerInfernoDrop,
    completeNapalm,
    infernoDropStrategemId: INFERNO_DROP_STRATEGEM_ID,
  };
};

export default useInfernoDropNapalm;
